import { getFormProps, getInputProps, type SubmissionResult, useForm } from '@conform-to/react';
import { getZodConstraint, parseWithZod } from '@conform-to/zod';
import { Form } from '@remix-run/react';
import { HoneypotInputs } from 'remix-utils/honeypot/react';
import { z } from 'zod';
import { Box } from '#app/components/Box.js';
import { ErrorList, Field } from '#app/components/forms.tsx';
import { HorizontalSeparator } from '#app/components/ui/Separator.js';
import { StatusButton } from '#app/components/ui/status-button.tsx';
import { ProviderConnectionForm, providerNames } from '#app/utils/connections.tsx';
import { EmailSchema, FirstNameSchema, LastNameSchema } from '#app/utils/user-validation.ts';

export const SignupSchema = z.object({
  email: EmailSchema,
  firstName: FirstNameSchema,
  lastName: LastNameSchema,
});

export interface SignupFormProps {
  lastResult?: SubmissionResult<string[]> | null;
  isPending?: boolean;
  redirectTo?: string | null;
  prefill?: Partial<{ firstName: string; lastName: string; email: string }>;
}

export function SignupForm({ lastResult, isPending = false, redirectTo, prefill }: SignupFormProps) {
  const [form, fields] = useForm<z.infer<typeof SignupSchema>>({
    id: 'signup-form-element',
    constraint: getZodConstraint(SignupSchema),
    lastResult,
    onValidate({ formData }) {
      return parseWithZod(formData, { schema: SignupSchema });
    },
    shouldRevalidate: 'onBlur',
    defaultValue: {
      firstName: prefill?.firstName ?? '',
      lastName: prefill?.lastName ?? '',
      email: prefill?.email ?? '',
    },
  });

  return (
    <Box className="mx-auto mt-4 min-w-full max-w-sm sm:min-w-[368px]">
      <Form method="POST" {...getFormProps(form)}>
        <HoneypotInputs />
        <div id="signup-email">
          <Field
            labelProps={{ htmlFor: fields.email.id, children: 'Email' }}
            inputProps={{
              ...getInputProps(fields.email, { type: 'email' }),
              autoFocus: true,
              autoComplete: 'email',
            }}
            errors={fields.email.errors}
          />
        </div>
        <div id="signup-firstName">
          <Field
            labelProps={{ htmlFor: fields.firstName.id, children: 'First Name' }}
            inputProps={{
              ...getInputProps(fields.firstName, { type: 'text' }),
              autoComplete: 'given-name',
            }}
            errors={fields.firstName.errors}
          />
        </div>
        <div id="signup-lastName">
          <Field
            labelProps={{ htmlFor: fields.lastName.id, children: 'Last Name' }}
            inputProps={{
              ...getInputProps(fields.lastName, { type: 'text' }),
              autoComplete: 'family-name',
            }}
            errors={fields.lastName.errors}
          />
        </div>
        {redirectTo ? <input type="hidden" name="redirectTo" value={redirectTo} /> : null}
        <ErrorList errors={form.errors} id={form.errorId} />
        <StatusButton
          id="signup-submit"
          className="bg-yankees-blue mt-4 w-full text-white"
          status={isPending ? 'pending' : form.status ?? 'idle'}
          type="submit"
          disabled={isPending}
        >
          Submit
        </StatusButton>
      </Form>
      <HorizontalSeparator margin="24px 0" />
      <ul className="flex flex-col gap-4">
        {providerNames.map((providerName) => (
          <li key={providerName}>
            <ProviderConnectionForm type="Signup" providerName={providerName} redirectTo={redirectTo} />
          </li>
        ))}
      </ul>
    </Box>
  );
}
